import type { Dispatch } from "redux";
import type { PaymentDeal } from "~/detail-pages/f2100/private/spareavtale/SpareavtaleDetails";
import actions from "~/shared/actions";
import * as server from "~/shared/api/server";
import type { Kapitalforvalter } from "~/shared/api/usePolicyDetails";
import type { ForvaltningsForm } from "~/shared/components/investeringsvalg/forvaltningsformer";
import dispatchers from "~/shared/dispatchers";
import type { State } from "~/shared/reducers";
import type { Policy } from "~/shared/reducers/policies/types";
import { period as holdingsHistoryPeriod } from "~/utils/policies";

export function setBeneficiaries(polisenummer: number, data: any) {
	return (dispatch: Dispatch) => {
		dispatch(actions.updateBeneficiariesRequest({ polisenummer }));
		return server.setBeneficiaries(polisenummer, data).then((result) => dispatch(actions.updateBeneficiariesResponse({ ...result, polisenummer })));
	};
}

export function loadPolicies() {
	return async (dispatch: Dispatch) => {
		dispatch(actions.policiesRequest());
		const response = await server.getPolicies();
		return dispatch(actions.policiesResponse(response));
	};
}

export function loadAggregatedPolicy(polisenummer: number, retryLoadPolicies = true) {
	return (dispatch: Dispatch, getState: () => State): Promise<any> => {
		dispatch(actions.aggregatedPolicyRequest({ polisenummer }));
		const { policies } = getState();
		const policy = policies.list.find((p: Policy) => p.polisenummer === polisenummer);
		if (!policy) {
			if (retryLoadPolicies) {
				return loadPolicies()(dispatch).then(() => loadAggregatedPolicy(polisenummer, false)(dispatch, getState));
			}
			return Promise.resolve(dispatchers.dispatchError(new Error(`Unknown policy: ${polisenummer}`))(dispatch));
		}
		dispatchers.loadHoldingsHistory(polisenummer, holdingsHistoryPeriod.SIX_MONTHS)(dispatch);

		return Promise.all([server.getPolicy(polisenummer), server.getHoldings(polisenummer)])
			.then(([policyData, holdings]) => ({ ...policyData, holdings }))
			.then((json) =>
				dispatch(
					actions.aggregatedPolicyResponse({
						polisenummer,
						policy: {
							...json,
							isComplete: true,
						},
					}),
				),
			);
	};
}

export function loadPolicyDetails(polisenummer: number, retryLoadPolicies = true) {
	return (dispatch: Dispatch, getState: () => State): Promise<any> => {
		dispatch(actions.policyDetailsRequest(polisenummer));
		const { policies } = getState();
		const policy = policies.list.find((p) => p.polisenummer === polisenummer);
		if (!policy) {
			if (retryLoadPolicies) {
				return loadPolicies()(dispatch).then(() => loadPolicyDetails(polisenummer, false)(dispatch, getState));
			}
			return Promise.resolve();
		}
		return server.getPolicy(polisenummer).then((json) =>
			dispatch(
				actions.policyDetailsResponse({
					policy: {
						...json,
						isComplete: true,
					},
				}),
			),
		);
	};
}

export function loadNjordPolicyDetails(polisenummer: number) {
	return async (dispatch: Dispatch) => {
		dispatch(actions.njordPolicyDetailsRequest(polisenummer));
		const response = await server.getNjordPolicy(polisenummer);
		return dispatch(actions.njordPolicyDetailsResponse(response));
	};
}

export function loadHoldings(polisenummer: number) {
	return (dispatch: Dispatch) => {
		dispatch(actions.holdingsRequest(polisenummer));
		return server.getHoldings(polisenummer).then((json) =>
			dispatch(
				actions.holdingsResponse({
					polisenummer,
					holdings: { ...json },
				}),
			),
		);
	};
}

export function loadFictiveUtbetalingMarketValue(polisenummer: number) {
	return (dispatch: Dispatch) => {
		dispatch(actions.fictiveUtbetalingMarketValueRequest(polisenummer));
		return server.getFictiveUtbetalingMarketValue(polisenummer).then((json) =>
			dispatch(
				actions.fictiveUtbetalingMarketValueResponse({
					polisenummer,
					fictiveUtbetalingMarketValue: json.marketValue,
				}),
			),
		);
	};
}

export function stopSavings(polisenummer: number) {
	return (dispatch: Dispatch) => {
		dispatch(actions.stopSavingsRequest());
		return server.stopSavings(polisenummer).then(({ manualStepsRequired, errorCode }) => {
			if (errorCode) {
				dispatch(
					actions.stopSavingsFailure({
						polisenummer,
						errorCode,
					}),
				);
			} else {
				dispatch(
					actions.stopSavingsResponse({
						polisenummer,
						savingAmount: 0,
						manualStepsRequired,
					}),
				);
			}
		});
	};
}

export function setSavingAmount(polisenummer: number, savingAmount: number) {
	return (dispatch: Dispatch) => {
		dispatch(actions.updateSavingAmountRequest());
		return server.setSavingAmount(polisenummer, savingAmount).then(({ manualStepsRequired, errorCode }) => {
			if (errorCode) {
				return dispatch(
					actions.updateSavingAmountFailure({
						polisenummer,
						errorCode,
					}),
				);
			}
			return dispatch(
				actions.updateSavingAmountResponse({
					polisenummer,
					savingAmount,
					manualStepsRequired,
				}),
			);
		});
	};
}

export function updateKapitalforvalter(polisenummer: number, kapitalforvalter: Kapitalforvalter) {
	return (dispatch: Dispatch, getState: () => State) => {
		dispatch(
			actions.updateKapitalforvalterRequest({
				polisenummer,
				kapitalforvalter,
			}),
		);
		const policy = getState().policies.list.find((p) => p.polisenummer === polisenummer);
		return server
			.updateKapitalforvalter(polisenummer, kapitalforvalter)
			.then(() => dispatchers.loadPolicyDetails(polisenummer)(dispatch, getState))
			.then(() => dispatchers.loadHoldings(polisenummer)(dispatch))
			.then(() => dispatchers.loadPolicyPrices(polisenummer, policy?.produktId)(dispatch))
			.then(() =>
				dispatch(
					actions.updateKapitalforvalterResponse({
						polisenummer,
						kapitalforvalter,
					}),
				),
			);
	};
}

export function updateNedtrapping(polisenummer: number, nedtrapping: boolean) {
	return (dispatch: Dispatch, getState: () => State) => {
		dispatch(
			actions.updateNedtrappingRequest({
				polisenummer,
				nedtrapping,
			}),
		);
		const policy = getState().policies.list.find((p: Policy) => p.polisenummer === polisenummer);
		return server
			.updateNedtrapping(polisenummer, nedtrapping)
			.then(() => dispatchers.loadPolicyDetails(polisenummer)(dispatch, getState))
			.then(() => dispatchers.loadHoldings(polisenummer)(dispatch))
			.then(() => {
				if (policy?.sb1PrognoseStotte) {
					return dispatchers.loadPolicyPrognosis({ polisenummer })(dispatch);
				}
			})
			.then(() => dispatchers.loadPolicyPrices(polisenummer, policy?.produktId)(dispatch))
			.then(() => {
				dispatch(
					actions.updateNedtrappingResponse({
						polisenummer,
						nedtrapping,
					}),
				);
			});
	};
}

export function updateForvaltning(polisenummer: number, forvaltning: ForvaltningsForm) {
	return (dispatch: Dispatch, getState: () => State) => {
		dispatch(actions.updateForvaltningRequest());
		const policy = getState().policies.list.find((p: Policy) => p.polisenummer === polisenummer);
		return server
			.updateForvaltning(polisenummer, forvaltning)
			.then(() => dispatchers.loadPolicyDetails(polisenummer)(dispatch, getState))
			.then(() => dispatchers.loadHoldings(polisenummer)(dispatch))
			.then(() => {
				if (policy?.sb1PrognoseStotte) {
					return dispatchers.loadPolicyPrognosis({ polisenummer })(dispatch);
				}
			})
			.then(() => dispatchers.loadPolicyPrices(polisenummer, policy?.produktId)(dispatch))
			.then(() => {
				dispatch(actions.updateForvaltningResponse({ polisenummer, forvaltning }));
			});
	};
}

export function getSalaryHistory(polisenummer: number) {
	return async (dispatch: Dispatch) => {
		dispatch(actions.salaryHistoryRequest({ polisenummer }));
		return server.getSalaryHistory(polisenummer).then((salaryHistory) =>
			dispatch(
				actions.salaryHistoryResponse({
					polisenummer,
					salaryHistory,
				}),
			),
		);
	};
}

export function updatePaymentDeal(polisenummer: number, paymentDeal: PaymentDeal) {
	return (dispatch: Dispatch) => {
		dispatch(
			actions.updatePaymentDealRequest({
				polisenummer,
				paymentDeal,
			}),
		);
		return server.updatePaymentDeal(paymentDeal).then(({ success }) =>
			dispatch(
				actions.updatePaymentDealResponse({
					polisenummer,
					paymentDeal,
					success,
				}),
			),
		);
	};
}

export function deletePaymentDeal(polisenummer: number, kid: string) {
	return (dispatch: Dispatch) => {
		dispatch(
			actions.deletePaymentDealRequest({
				polisenummer,
				kid,
			}),
		);
		return server.deletePaymentDeal(kid).then(() =>
			dispatch(
				actions.deletePaymentDealResponse({
					polisenummer,
					kid,
				}),
			),
		);
	};
}

export function createPaymentDeal(polisenummer: number, paymentDeal: PaymentDeal) {
	return (dispatch: Dispatch) => {
		dispatch(
			actions.createPaymentDealRequest({
				polisenummer,
				paymentDeal,
			}),
		);
		return server.createPaymentDeal(paymentDeal).then(({ success }) =>
			dispatch(
				actions.createPaymentDealResponse({
					polisenummer,
					paymentDeal,
					success,
				}),
			),
		);
	};
}

export function updateUtbetalingStart(polisenummer: number, data: any) {
	return (dispatch: Dispatch) => {
		dispatch(
			actions.updateUtbetalingStartRequest({
				polisenummer,
				data,
			}),
		);
		return server.updateUtbetalingStart(polisenummer, data).then(() =>
			dispatch(
				actions.updateUtbetalingStartResponse({
					polisenummer,
					data,
				}),
			),
		);
	};
}

export function loProduktbytte() {
	return (dispatch: Dispatch) => {
		dispatch(actions.loProduktbytteRequest());
		return server.loProduktbytte().then(() => dispatch(actions.loProduktbytteResponse()));
	};
}

export function loadIpsDeposit(polisenummer: number) {
	return (dispatch: Dispatch) => {
		dispatch(actions.loadIpsDepositRequest());
		return server.loadIpsDeposit(polisenummer).then((deposit) => dispatch(actions.loadIpsDepositResponse({ polisenummer, deposit })));
	};
}

export function registerDirectPayment(polisenummer: number, data: any) {
	return (dispatch: Dispatch) => {
		dispatch(actions.directPaymentRequest({ polisenummer, ...data }));
		return server
			.registerDirectPayment(polisenummer, {
				...data,
				amount: Number(data.amount.replace(/\s/g, "")),
			})
			.then((response) => dispatch(actions.directPaymentResponse(response)));
	};
}

export function resetPolicies() {
	return (dispatch: Dispatch) => dispatch(actions.resetPolicies());
}
